import { forwardRef, useImperativeHandle, useRef } from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { formatDate } from "@roc/feature-utils";
import { observer } from "mobx-react";
import { PhotoCover } from "@roc/ui";
import { FlagOutlined } from "@material-ui/icons";
import reportedPhotoThumbnail from '../assets/reportedPhotoThumbnail.png';

interface StyleProps {
  hasIssues: boolean;
  hasManualValidation: boolean;
  isReported?: boolean;
  showStatusIcon?: boolean;
}

const useStyles = makeStyles(() => ({
  photoCard: ({ hasIssues, hasManualValidation, showStatusIcon }: StyleProps) => ({
    cursor: "pointer",
    transition: "transform 0.3s ease",
    border: "2px solid",
    borderColor: !showStatusIcon ? "#cdcdcd" : hasIssues ? "#dd3030" : hasManualValidation ? "#FB8C00" : "#079669",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    "&.animate": {
      animation: "$pulse 0.6s ease",
    },
  }),
  "@keyframes pulse": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.1)" },
    "100%": { transform: "scale(1)" },
  },
  photoCardInfoContainer: ({ isReported = false }: StyleProps) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: isReported ? "4px 2px" : "4px 8px",
  }),
  photoCardInfo: {
    fontSize: "14px",
  },
  reportedIconContainer: {
    width: "20px",
    height: "20px",
    backgroundColor: "#dd3030",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "4px 0",
    borderRadius: "50%",
  },
  reportedIcon: {
    fontSize: "15px",
    color: "#fff",
    display: "block",
  }
}));

interface PhotoCardProps {
  url: string;
  hasIssues: boolean;
  isReported?: boolean;
  hasManualValidation: boolean;
  serverDate: Date;
  onClick?: () => void;
  preferredRotation?: number;
  caption?: string;
  showStatusIcon?: boolean;
}

export interface PhotoCardHandle {
  triggerAnimation: () => void;
}

export const PhotoCard = observer(forwardRef<PhotoCardHandle, PhotoCardProps>(
  ({ url, hasIssues, hasManualValidation, serverDate, onClick, preferredRotation = 0, caption, isReported, showStatusIcon = true }, ref) => {
    const classes = useStyles({ hasIssues, hasManualValidation, isReported, showStatusIcon });
    const cardRef = useRef<HTMLDivElement>(null);

    const clickHandler = () => {
      onClick && onClick();
    };

    useImperativeHandle(ref, () => ({
      triggerAnimation: () => {
        if (cardRef.current) {
          cardRef.current.classList.add("animate");
          setTimeout(() => {
            cardRef.current?.classList.remove("animate");
          }, 600);
        }
      },
    }));

    return (
      <div ref={cardRef} className={classes.photoCard} onClick={clickHandler}>
        <PhotoCover
          url={isReported ? reportedPhotoThumbnail : url}
          rotationDegrees={isReported ? 0 : preferredRotation}
          width="208px"
          height="170px"
          caption={caption}
        />
        <Box className={classes.photoCardInfoContainer}>
          <Typography className={classes.photoCardInfo}>
            {formatDate(serverDate, "MM-dd-yyyy hh:mm:ss aaa")}
          </Typography>
          {showStatusIcon &&
            (hasIssues ? (
              <CancelIcon style={{ color: "#dd3030" }} />
            ) : hasManualValidation ? (
              <CheckCircleIcon style={{ color: "#FB8C00" }} />
            ) : (
              <CheckCircleIcon style={{ color: "#079669" }} />
            ))}
          {isReported && (
            <Box className={classes.reportedIconContainer}>
              < FlagOutlined className={classes.reportedIcon} />
            </Box>
          )}
        </Box>
      </div>
    );
  }
));
